import React, { useState, useEffect } from 'react';
import { ShoppingCart, Ban, Save } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { useOrderingEnabled, DEFAULT_ORDERING_MESSAGE } from '../../hooks/useOrderingEnabled';

/**
 * Admin control for globally enabling/disabling online ordering.
 * When disabled, all users can still search, price and check availability,
 * but cannot add to cart or place orders (portal or REST API).
 */
const OrderingSettingsCard = () => {
  const { enabled, message, loading: settingsLoading, refresh } = useOrderingEnabled();
  const [draftMessage, setDraftMessage] = useState(message);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    setDraftMessage(message);
  }, [message]);

  const save = async (nextEnabled) => {
    try {
      setSaving(true);
      setConfirming(null);

      const { error } = await supabase.rpc('update_ordering_settings', {
        p_enabled: nextEnabled,
        p_message: draftMessage
      });

      if (error) throw error;

      showNotification(
        nextEnabled === enabled
          ? 'Ordering message saved'
          : `Online ordering ${nextEnabled ? 'enabled' : 'disabled'}`,
        'success'
      );
      refresh();
    } catch (error) {
      console.error('Error updating ordering settings:', error);
      showError(error, 'Failed to update ordering settings');
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return <div className="h-40 bg-gray-200 rounded-lg animate-pulse" />;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className={`${enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b rounded-t-lg px-6 py-5 flex items-center justify-between`}>
        <div className="flex items-center space-x-4">
          <div className={`${enabled ? 'bg-green-500' : 'bg-red-500'} rounded-full p-3`}>
            {enabled ? <ShoppingCart className="h-6 w-6 text-white" /> : <Ban className="h-6 w-6 text-white" />}
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${enabled ? 'text-green-900' : 'text-red-900'}`}>
              Online Ordering: {enabled ? 'Enabled' : 'Disabled'}
            </h3>
            <p className={`text-sm ${enabled ? 'text-green-700' : 'text-red-700'}`}>
              {enabled
                ? 'Users can add to cart and place orders.'
                : 'Users can check pricing and availability, but cannot add to cart or place orders (portal and API).'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setConfirming(!enabled)}
          disabled={saving}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white disabled:opacity-50 ${
            enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {enabled ? <Ban className="mr-2 h-4 w-4" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
          {enabled ? 'Disable Ordering' : 'Enable Ordering'}
        </button>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label htmlFor="ordering_message" className="block text-sm font-medium text-gray-700 mb-2">
            Message shown to users while ordering is disabled
          </label>
          <textarea
            id="ordering_message"
            rows={3}
            value={draftMessage}
            onChange={(e) => setDraftMessage(e.target.value)}
            placeholder={DEFAULT_ORDERING_MESSAGE}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand sm:text-sm"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => save(enabled)}
            disabled={saving || draftMessage === message}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-black bg-brand hover:bg-brand-hover disabled:opacity-50"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Message'}
          </button>
        </div>
      </div>

      {confirming !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-medium text-gray-900">
              {confirming ? 'Enable online ordering?' : 'Disable online ordering?'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {confirming
                ? 'Users will immediately be able to add to cart and place orders again.'
                : 'All users (including staff and admins) will immediately be unable to add to cart or place orders, via the portal and the REST API. Pricing and availability remain available.'}
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setConfirming(null)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => save(confirming)}
                disabled={saving}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                  confirming ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {saving ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderingSettingsCard;
